/** @jsx jsx */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { Intl, useDebounce } from '@erecruit/core';
import { Resources, AppPermissions } from '@erecruit/types';
import { Authorize } from '@erecruit/modules';

import {
  Container,
  Flex,
  Search,
  jsx,
  A,
  styled,
  FlexItem,
} from '@erecruit/ui-kit';
import EmptyStateContainer from '../EmptyStateContainer';
import {
  selectors,
  fetchCredentials,
  fetchDetails,
  fetchAttachments,
  downloadAllAttachments,
} from '../../state/reducer';
import { RootState } from '../../../../redux/reducers';
import CredentialsCard from './CredentialCard';
import {
  Details,
  Credentials as CredentialProps,
  Attachments,
  MatchRecordActions,
} from '../../types';

import { TabSpinner } from './CredentialSpinners';

const { FormattedMessage } = Intl;

export type CredentialsTabsProps = Details &
  CredentialProps &
  Attachments & {
    fetchCredentials: MatchRecordActions['fetchCredentials'];
    fetchDetails: MatchRecordActions['fetchDetails'];
    fetchAttachments: MatchRecordActions['fetchAttachments'];
    downloadAllAttachments: MatchRecordActions['downloadAllAttachments'];
  };

const CredentialsWrapper = styled(Container)({
  //TODO: need to find solution for tabs layout
  height: 'calc(100vh - (64px + 16px + 101px + 43px))',
});

export const Credentials = ({
  isLoading,
  data,
  fetchCredentials,
  fetchDetails,
  isLoadingDetails,
  details,
  fetchAttachments,
  isLoadingAttachments,
  attachments,
  downloadAllAttachments,
}: CredentialsTabsProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const { id: matchId } = useParams();

  useEffect(() => {
    fetchCredentials(Number(matchId));
  }, [fetchCredentials, matchId]);

  const handleDetails = (
    id: Resources.MatchRequirementProgress['requirementId'],
  ) => {
    fetchDetails(id);
  };

  const handleAttachment = (
    id: Resources.MatchRequirementProgress['requirementId'],
  ) => {
    fetchAttachments(id);
  };

  const handleAttachmentDownload = () => {
    downloadAllAttachments(data.map(credential => credential.requirementId));
  };

  return (
    <CredentialsWrapper padding={12}>
      {isLoading ? (
        <TabSpinner />
      ) : (
        <Flex
          direction="column"
          gutter={6}
          gutterDirection="vertical"
          padding={[0, 12]}
        >
          {!data.length ? (
            <EmptyStateContainer
              text={
                <FormattedMessage
                  id={messageIds.vms.matchRecord.credentials.emptyState}
                  values={{ matchId }}
                />
              }
            />
          ) : (
            <React.Fragment>
              <FlexItem align="center">
                <Search
                  placeholder="Search by name or ID"
                  onChange={e => setSearchTerm(e.target.value)}
                  value={searchTerm}
                />
              </FlexItem>
              <Authorize
                permission={AppPermissions.PreviewAndDownloadMatchCredentials}
              >
                <FlexItem align="flex-end">
                  <A weight={600} onClick={handleAttachmentDownload}>
                    <FormattedMessage
                      id={messageIds.vms.matchRecord.credentials.downloadAll}
                    />
                  </A>
                </FlexItem>
              </Authorize>
              <Flex direction="column" gutterDirection="vertical" gutter={2}>
                {data
                  .filter(element =>
                    !debouncedSearchTerm
                      ? element
                      : element.name
                          .toLowerCase()
                          .includes(debouncedSearchTerm.toLowerCase()),
                  )
                  .map((element: Resources.MatchRequirementProgress) => {
                    return (
                      <CredentialsCard
                        key={element.requirementId}
                        data={element}
                        handleDetails={handleDetails}
                        isLoadingDetails={isLoadingDetails}
                        details={details}
                        handleAttachment={handleAttachment}
                        isLoadingAttachments={isLoadingAttachments}
                        attachments={attachments}
                      />
                    );
                  })}
              </Flex>
            </React.Fragment>
          )}
        </Flex>
      )}
    </CredentialsWrapper>
  );
};

export default connect(
  (state: RootState) => ({
    isLoading: selectors.selectMatchRecord(state).credentials.isLoading,
    data: selectors.selectMatchRecord(state).credentials.data,
    isLoadingDetails: selectors.selectMatchRecord(state).credentials
      .isLoadingDetails,
    details: selectors.selectMatchRecord(state).credentials.details,
    isLoadingAttachments: selectors.selectMatchRecord(state).credentials
      .isLoadingAttachments,
    attachments: selectors.selectMatchRecord(state).credentials.attachments,
  }),
  { fetchCredentials, fetchDetails, fetchAttachments, downloadAllAttachments },
)(Credentials);
